import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { track } from '@lf/analytics-events';

import SavedView from '../cards/home/SavedView';
import SearchView from '../cards/home/SearchView';
import { getYouTubeSearch } from '../api/lfApiYouTube';
import { parseSearchInput } from '../lib/home/parseSearchInput';
import { transformSavedVideos } from '../lib/home/savedVideoTransform';
import { useSavedVideoList } from '../lib/videos/useSavedVideoList';
import { useError } from '../contexts/ErrorContext';

function _randomId() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `sq_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
}

const HomePage = ({ disableInteractiveFeatures = false }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [videos, setVideos] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [activeView, setActiveView] = useState('search');

  const latestQueryRef = useRef('');
  // Per-search-session UUID threaded through search_submitted →
  // search_results_returned → search_result_clicked / search_abandoned.
  // Generated client-side per design §F: literal query text never
  // leaves the browser; only its length and this opaque id.
  const searchQueryIdRef = useRef(null);
  const resultClickedRef = useRef(false);

  const { setError } = useError();
  const navigate = useNavigate();
  const location = useLocation();

  const {
    savedVideos,
    savedVideoIds,
    isLoading: isLoadingSaved,
    handleSaveToggle,
  } = useSavedVideoList({ setError });

  const performSearch = useCallback(
    async (query) => {
      const trimmedQuery = query.trim();
      const { query: normalizedQuery, channelId } = parseSearchInput(trimmedQuery);

      if (!normalizedQuery && !channelId) {
        latestQueryRef.current = '';
        setVideos([]);
        setHasSearched(false);
        setIsLoading(false);
        return;
      }

      latestQueryRef.current = trimmedQuery;
      // New search session = new search_query_id. Mark "no click yet"
      // for the session so navigation away fires `search_abandoned`.
      const searchQueryId = _randomId();
      searchQueryIdRef.current = searchQueryId;
      resultClickedRef.current = false;
      track('search_submitted', {
        search_query_id: searchQueryId,
        query_length: trimmedQuery.length,
        from_page: 'home',
      });
      const startedAt = Date.now();
      setIsLoading(true);
      try {
        const { results } = await getYouTubeSearch(
          normalizedQuery,
          setError,
          undefined,
          channelId || undefined,
        );
        if (latestQueryRef.current !== trimmedQuery) {
          return;
        }

        setVideos(results);
        setHasSearched(true);
        track('search_results_returned', {
          search_query_id: searchQueryId,
          result_count: results.length,
          latency_ms: Date.now() - startedAt,
          is_zero_result: results.length === 0 ? 1 : 0,
        });
      } finally {
        if (latestQueryRef.current === trimmedQuery) {
          setIsLoading(false);
        }
      }
    },
    [setError],
  );

  const savedVideoCards = useMemo(() => transformSavedVideos(savedVideos), [savedVideos]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const queryParam = params.get('q') || '';

    setSearchTerm(queryParam);
    latestQueryRef.current = queryParam.trim();

    if (queryParam) {
      performSearch(queryParam);
    } else {
      setVideos([]);
      setHasSearched(false);
      setIsLoading(false);
    }
  }, [location.search, performSearch]);

  const applySearchQuery = useCallback(
    (query) => {
      const trimmedValue = query.trim();
      const params = new URLSearchParams(location.search);
      const currentQuery = params.get('q') || '';

      if (trimmedValue) {
        if (trimmedValue === currentQuery.trim()) {
          performSearch(trimmedValue);
          return;
        }

        params.set('q', trimmedValue);
      } else {
        params.delete('q');
      }

      const nextSearch = params.toString();
      const nextLocation = nextSearch ? `${location.pathname}?${nextSearch}` : location.pathname;

      navigate(nextLocation, { replace: false });
    },
    [location.pathname, location.search, navigate, performSearch],
  );

  const handleChannelSelect = useCallback(
    (channelId) => {
      if (!channelId) {
        return;
      }

      const channelQuery = `channelId:${channelId}`;
      setSearchTerm(channelQuery);

      const params = new URLSearchParams(location.search);
      const currentQuery = params.get('q') || '';

      if (channelQuery === currentQuery.trim()) {
        performSearch(channelQuery);
        return;
      }

      params.set('q', channelQuery);
      const nextSearch = params.toString();
      const nextLocation = nextSearch ? `${location.pathname}?${nextSearch}` : location.pathname;

      navigate(nextLocation, { replace: false });
    },
    [location.pathname, location.search, navigate, performSearch],
  );

  const handleClearSearch = useCallback(() => {
    setSearchTerm('');
    latestQueryRef.current = '';
    setVideos([]);
    setHasSearched(false);
    setIsLoading(false);

    const params = new URLSearchParams(location.search);
    if (!params.get('q')) {
      return;
    }

    params.delete('q');
    const nextSearch = params.toString();
    const nextLocation = nextSearch ? `${location.pathname}?${nextSearch}` : location.pathname;

    navigate(nextLocation, { replace: false });
  }, [location.pathname, location.search, navigate]);

  const handleVideoSelect = (video, options = {}) => {
    const videoId = video.id?.videoId;
    const channelId = video.snippet?.channelId;

    if (!videoId) {
      return;
    }

    // Determine the surface that initiated the play. SearchView clicks
    // pass options.fromSearch=true; SavedView clicks pass
    // options.fromSaved=true. Default surface is `home` because
    // HomePage *is* the test-yted home surface.
    let source = 'home';
    if (options.fromSearch) source = 'search';
    else if (options.fromSaved) source = 'saved';

    if (source === 'search' && hasSearched && videos.length > 0) {
      const position = videos.findIndex((v) => v.id?.videoId === videoId);
      const sqid = searchQueryIdRef.current;
      if (sqid) {
        resultClickedRef.current = true;
        track('search_result_clicked', {
          search_query_id: sqid,
          result_position: position >= 0 ? position : 0,
          video_id: videoId,
        });
      }
    }

    const params = new URLSearchParams();
    params.set('vid', videoId);
    if (channelId) {
      params.set('cid', channelId);
    }
    params.set('src', source);
    if (source === 'search' && searchQueryIdRef.current) {
      params.set('sqid', searchQueryIdRef.current);
    }
    if (video.snippet?.title) {
      params.set('title', video.snippet.title);
    }
    if (video.snippet?.channelTitle) {
      params.set('ctitle', video.snippet.channelTitle);
    }
    const thumbUrl =
      video.snippet?.thumbnails?.high?.url ||
      video.snippet?.thumbnails?.medium?.url ||
      video.snippet?.thumbnails?.default?.url ||
      '';
    if (thumbUrl) {
      params.set('thumb', thumbUrl);
    }
    if (disableInteractiveFeatures) {
      params.set('lite', '1');
    }

    navigate(`/video?${params.toString()}`);
  };

  // Fire `search_abandoned` if the user navigates away without
  // clicking a result. Refs avoid re-registering on every keystroke.
  useEffect(() => {
    return () => {
      const sqid = searchQueryIdRef.current;
      if (sqid && !resultClickedRef.current) {
        track('search_abandoned', {
          search_query_id: sqid,
          had_results: videos.length > 0 ? 1 : 0,
        });
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="flex flex-col gap-6">
      <div className="flex justify-center pt-6">
        <div className="border border-gray-300 bg-white rounded-2xl flex flex-row overflow-hidden">
          <button
            type="button"
            className={`px-4 py-2 text-sm ${
              activeView === 'search'
                ? 'text-white bg-blue-500'
                : 'text-gray-600 hover:text-gray-800'
            }`}
            onClick={() => setActiveView('search')}
          >
            Search
          </button>
          <button
            type="button"
            className={`px-4 py-2 text-sm ${
              activeView === 'saved'
                ? 'text-white bg-blue-500'
                : 'text-gray-600 hover:text-gray-800'
            }`}
            onClick={() => setActiveView('saved')}
          >
            Saved
          </button>
        </div>
      </div>

      {activeView === 'search' && (
        <SearchView
          searchTerm={searchTerm}
          onChange={setSearchTerm}
          onSubmit={applySearchQuery}
          onClear={handleClearSearch}
          isLoading={isLoading}
          videos={videos}
          hasSearched={hasSearched}
          savedVideoIds={savedVideoIds}
          onVideoSelect={(video) => handleVideoSelect(video, { fromSearch: true })}
          onChannelSelect={handleChannelSelect}
          onSaveToggle={handleSaveToggle}
        />
      )}

      {activeView === 'saved' && (
        <SavedView
          isLoading={isLoadingSaved}
          videoCards={savedVideoCards}
          onVideoSelect={(video) => handleVideoSelect(video, { fromSaved: true })}
          onChannelSelect={handleChannelSelect}
          onSaveToggle={handleSaveToggle}
        />
      )}
    </div>
  );
};

export default HomePage;
